import { AddressType, DataType, PersonOrgRelationType, PersonOrgType } from '@ndvr/customer-api';

import { TaxIdCell } from 'views/household/HouseholdMembersTab/TaxIdCell';

import { AddressFields } from './AddressFields';
import { DateField, Field, FieldGroup, SelectField, TextField } from './fields';
import {
    arePercentsComplete,
    changedAddress,
    CUSTOM_ROLE,
    data,
    ENTITY_TYPES,
    PersonKind,
    professionalRole,
    RelatedPerson,
    RELATION_LABELS,
    relationTo,
    requiredAddressParts,
    roleLabelOf,
    text,
    toAddressValues,
    todayInput,
    toEntityLinks,
    toProfileSaveRequest,
    toRelatedPeople,
    toRoleChoice,
} from './onboarding-model';
import {
    useInvalidatePeople,
    useSaveOnboardingProfile,
    useSetHouseholdRoleLabel,
    useSetRelation,
} from './onboarding-tab-queries';
import { PersonCardProps, PersonCardShell } from './PersonCardShell';
import { ProfessionalRoleFields } from './ProfessionalRoleFields';
import { RelatedPeopleRows } from './RelatedPeopleRows';
import { useAutosave } from './useAutosave';

const RELATION_OPTIONS = Object.values(PersonOrgRelationType).map((value) => ({
    value,
    label: RELATION_LABELS[value],
}));

/**
 * Family: name and relationship always; DOB and SSN once they are a beneficiary, a phone once they are someone's
 * trusted contact. The relationship is what they are to the household's primary client.
 */
export const FamilyCard = (props: PersonCardProps) => {
    const { profile, householdGUID, household } = props;
    const guid = profile.personOrgGUID;
    const anchor = household.primary?.personOrgGUID;
    const save = useSaveOnboardingProfile(householdGUID);
    const refreshPeople = useInvalidatePeople(householdGUID);
    const setRelation = useSetRelation();
    const { values, setValues, autosave } = useAutosave({
        stored: {
            firstName: text(profile.personOrg.firstName),
            middleName: text(profile.personOrg.middleName),
            lastName: text(profile.personOrg.lastName),
            relation: (anchor && relationTo(household.relations, anchor, guid)) || '',
            dateOfBirth: profile.personOrg.dateOfBirth?.plainText ?? '',
            email: text(profile.personOrg.primaryEmail),
            phone: text(profile.personOrg.personalPhone),
            address: toAddressValues(profile.primaryAddress),
        },
        /**
         * The relationship is saved first: the profile save reloads the stored relations, which must already hold the
         * new one. It is compared with the one last saved or loaded, so relations still loading never read as cleared.
         */
        save: async (form, saved) => {
            if (anchor && form.relation !== saved.relation) {
                await setRelation.mutateAsync({
                    anchorGUID: anchor,
                    personOrgGUID: guid,
                    relationType: (form.relation as PersonOrgRelationType) || undefined,
                });
            }
            await save.mutateAsync({
                personOrgGUID: guid,
                request: toProfileSaveRequest(
                    profile,
                    {
                        firstName: data(guid, DataType.PERSON_ORG_INFORMATION_FIRST_NAME, form.firstName),
                        middleName: data(guid, DataType.PERSON_ORG_INFORMATION_MIDDLE_NAME, form.middleName),
                        lastName: data(guid, DataType.PERSON_ORG_INFORMATION_LAST_NAME, form.lastName),
                        primaryEmail: data(guid, DataType.PERSON_ORG_EMAIL_ADDRESS, form.email),
                        personalPhone: data(guid, DataType.PERSON_ORG_PHONE_NUMBER, form.phone),
                        dateOfBirth: data(guid, DataType.PERSON_ORG_INFORMATION_DATE_OF_BIRTH, form.dateOfBirth),
                    },
                    { primary: changedAddress(guid, AddressType.PRIMARY, form.address, profile.primaryAddress) },
                ),
            });
        },
    });
    const set =
        <K extends keyof typeof values>(key: K) =>
        (value: (typeof values)[K]) =>
            setValues((current) => ({ ...current, [key]: value }));

    const isBeneficiary = household.involvement(guid).beneficiary.length > 0;
    const isContact = household.profiles.some((other) => other.personOrg.trustedContact?.personOrgGUID === guid);
    const missing = [
        values.firstName,
        values.lastName,
        !anchor || values.relation,
        !isBeneficiary || values.dateOfBirth,
        !isBeneficiary || profile.personOrg.identifyingTaxIdProvided,
        !isContact || values.phone,
    ].filter((value) => !value).length;

    return (
        <PersonCardShell {...props} kind={PersonKind.FAMILY} missing={missing} autosave={autosave}>
            <FieldGroup title="Details">
                <TextField label="First name" value={values.firstName} onChange={set('firstName')} required />
                <TextField label="Middle name" value={values.middleName} onChange={set('middleName')} />
                <TextField label="Last name" value={values.lastName} onChange={set('lastName')} required />
                <SelectField
                    label={`Relationship to ${household.nameOf(anchor) || 'the primary client'}`}
                    value={values.relation}
                    onChange={set('relation')}
                    options={RELATION_OPTIONS}
                    required={!!anchor}
                    disabled={!anchor}
                />
                <DateField
                    label="Date of birth"
                    value={values.dateOfBirth}
                    onChange={set('dateOfBirth')}
                    max={todayInput()}
                    required={isBeneficiary}
                />
                <Field label="SSN" missing={isBeneficiary && !profile.personOrg.identifyingTaxIdProvided}>
                    {() => <TaxIdCell personOrg={profile.personOrg} onSaved={refreshPeople} required={isBeneficiary} />}
                </Field>
            </FieldGroup>
            <FieldGroup title={isContact ? 'Contact' : 'Contact (optional)'}>
                <TextField label="Email" value={values.email} onChange={set('email')} />
                <TextField label="Phone" value={values.phone} onChange={set('phone')} required={isContact} />
                <AddressFields label="Address" value={values.address} onChange={set('address')} />
            </FieldGroup>
        </PersonCardShell>
    );
};

/** A trusted professional: their role is their household role label, their firm the employer name. */
export const ProfessionalCard = (props: PersonCardProps) => {
    const { profile, householdGUID } = props;
    const guid = profile.personOrgGUID;
    const save = useSaveOnboardingProfile(householdGUID);
    const setRole = useSetHouseholdRoleLabel(householdGUID);
    const { values, setValues, autosave } = useAutosave({
        stored: {
            firstName: text(profile.personOrg.firstName),
            middleName: text(profile.personOrg.middleName),
            lastName: text(profile.personOrg.lastName),
            role: toRoleChoice(professionalRole(profile, householdGUID)),
            firm: profile.personOrg.employerName ?? '',
            email: text(profile.personOrg.primaryEmail),
            phone: text(profile.personOrg.personalPhone),
        },
        check: (form) => {
            if (roleLabelOf(form.role)) return undefined;
            return form.role.role === CUSTOM_ROLE ? 'Enter the custom role' : 'Select a role';
        },
        save: (form, saved) => {
            const roleLabel = roleLabelOf(form.role);
            return Promise.all([
                save.mutateAsync({
                    personOrgGUID: guid,
                    request: toProfileSaveRequest(profile, {
                        firstName: data(guid, DataType.PERSON_ORG_INFORMATION_FIRST_NAME, form.firstName),
                        middleName: data(guid, DataType.PERSON_ORG_INFORMATION_MIDDLE_NAME, form.middleName),
                        lastName: data(guid, DataType.PERSON_ORG_INFORMATION_LAST_NAME, form.lastName),
                        employerName: form.firm.trim() || undefined,
                        primaryEmail: data(guid, DataType.PERSON_ORG_EMAIL_ADDRESS, form.email),
                        personalPhone: data(guid, DataType.PERSON_ORG_PHONE_NUMBER, form.phone),
                    }),
                }),
                roleLabel !== roleLabelOf(saved.role)
                    ? setRole.mutateAsync({ personOrgGUID: guid, roleLabel })
                    : undefined,
            ]);
        },
    });
    const set =
        <K extends keyof typeof values>(key: K) =>
        (value: (typeof values)[K]) =>
            setValues((current) => ({ ...current, [key]: value }));

    const missing = [values.firstName, values.lastName, roleLabelOf(values.role)].filter((value) => !value).length;

    return (
        <PersonCardShell {...props} kind={PersonKind.PROFESSIONAL} missing={missing} autosave={autosave}>
            <FieldGroup title="Details" columns={4}>
                <TextField label="First name" value={values.firstName} onChange={set('firstName')} required />
                <TextField label="Middle name" value={values.middleName} onChange={set('middleName')} />
                <TextField label="Last name" value={values.lastName} onChange={set('lastName')} required />
                <ProfessionalRoleFields value={values.role} onChange={set('role')} />
                <TextField label="Firm" value={values.firm} onChange={set('firm')} />
                <TextField label="Email" value={values.email} onChange={set('email')} />
                <TextField label="Phone" value={values.phone} onChange={set('phone')} />
            </FieldGroup>
        </PersonCardShell>
    );
};

export const EntityCard = (props: PersonCardProps) => {
    const { profile, householdGUID, household, addPerson } = props;
    const guid = profile.personOrgGUID;
    const save = useSaveOnboardingProfile(householdGUID);
    const refreshPeople = useInvalidatePeople(householdGUID);
    const entityType = profile.personOrg.personOrgType;
    const linkedRows = (related: RelatedPerson[]) => related.filter((row) => row.personOrgGUID);
    const { values, setValues, autosave } = useAutosave({
        stored: {
            subtype: profile.personOrg.personOrgSubtype ?? '',
            legalName: text(profile.personOrg.orgName),
            established: profile.personOrg.orgEstablishedAt?.plainText ?? '',
            state: profile.personOrg.orgState ?? '',
            address: toAddressValues(profile.primaryAddress),
            related: toRelatedPeople(guid, household.links),
        },
        // The backend refuses the whole link set unless each row has a role and each role's percents total 100
        check: (form) => {
            const rows = linkedRows(form.related);
            if (!rows.every((row) => row.role)) return 'Select a role for each related person';
            return arePercentsComplete(rows) ? undefined : 'Make each role’s percentages total 100%';
        },
        save: (form) =>
            save.mutateAsync({
                personOrgGUID: guid,
                request: toProfileSaveRequest(
                    profile,
                    {
                        personOrgSubtype: (form.subtype || undefined) as typeof profile.personOrg.personOrgSubtype,
                        orgName: data(guid, DataType.PERSON_ORG_INFORMATION_ORGANIZATION_NAME, form.legalName),
                        orgEstablishedAt: data(
                            guid,
                            DataType.PERSON_ORG_INFORMATION_ORGANIZATION_ESTABLISHED_AT,
                            form.established,
                        ),
                        orgState: form.state.trim() || undefined,
                    },
                    { primary: changedAddress(guid, AddressType.PRIMARY, form.address, profile.primaryAddress) },
                    // The whole link set in the same save: replaced in one transaction, types not shown here kept
                    toEntityLinks(guid, linkedRows(form.related), household.links),
                ),
            }),
    });
    const set =
        <K extends keyof typeof values>(key: K) =>
        (value: (typeof values)[K]) =>
            setValues((current) => ({ ...current, [key]: value }));
    const rows = linkedRows(values.related);

    const missing = [
        values.subtype,
        values.legalName,
        profile.personOrg.identifyingTaxIdProvided,
        values.established,
        values.state,
        ...requiredAddressParts(values.address),
        values.related.every((row) => row.personOrgGUID && row.role),
        arePercentsComplete(rows),
    ].filter((value) => !value).length;

    return (
        <PersonCardShell {...props} kind={PersonKind.ENTITY} missing={missing} autosave={autosave}>
            <FieldGroup title="Details">
                <SelectField
                    label="Type"
                    value={values.subtype}
                    onChange={set('subtype')}
                    // The type (trust or organization) is fixed once created; the subtype changes within it
                    options={ENTITY_TYPES.filter((type) => type.type === profile.personOrg.personOrgType)}
                    required
                />
                <TextField label="Legal name" value={values.legalName} onChange={set('legalName')} required />
                <Field
                    label={profile.personOrg.personOrgType === PersonOrgType.TRUST ? 'TIN' : 'EIN'}
                    missing={!profile.personOrg.identifyingTaxIdProvided}
                >
                    {() => <TaxIdCell personOrg={profile.personOrg} onSaved={refreshPeople} required />}
                </Field>
                <DateField
                    label="Date established"
                    value={values.established}
                    onChange={set('established')}
                    max={todayInput()}
                    required
                />
                <TextField label="State of organization" value={values.state} onChange={set('state')} required />
                <AddressFields label="Address" value={values.address} onChange={set('address')} required />
            </FieldGroup>
            <div className="border-cocoa-100 flex flex-col gap-2.5 border-b py-[18px]">
                <div className="flex items-center gap-4">
                    <span className="text-cocoa-400 text-xs font-medium tracking-[0.1em] uppercase">
                        Related people
                    </span>
                </div>
                <RelatedPeopleRows
                    entityType={entityType}
                    rows={values.related}
                    onChange={set('related')}
                    personOptions={household.peopleOf(PersonKind.CLIENT, PersonKind.FAMILY)}
                    onAddNew={(index) =>
                        addPerson(PersonKind.FAMILY, (personOrgGUID) =>
                            setValues((current) => ({
                                ...current,
                                related: current.related.map((row, j) =>
                                    j === index ? { ...row, personOrgGUID } : row,
                                ),
                            })),
                        )
                    }
                />
            </div>
        </PersonCardShell>
    );
};
